/**
 * Batch Translation types.
 *
 * A batch job sends a file of text units to the Translation Service API
 * and tracks progress until all items are translated or the job fails.
 */

export type BatchJobStatus = "queued" | "submitted" | "pending" | "completed" | "failed";

export interface TextUnit {
  text_id: string;
  text: string;
}

export interface StartBatchRequest {
  source_language: string;
  target_language: string;
  model: string;
  text_units: TextUnit[];
}

export interface StartBatchResponse {
  /**
   * The backend returns the job id as `jobId` (camelCase). `job_id` is kept as
   * an optional alias for robustness but is not sent by the current API.
   */
  job_id?: string;
  jobId?: string;
  uploadUrl?: string;
  uploadURL?: string;
  upload_url?: string;
  status?: string;
}

export interface BatchJobStatusResponse {
  global_status: string;
  id: string;
  translations?: Record<string, string> | null;
  presigned_urls?: Record<string, string> | null;
  validation?: Record<string, {
    language: string;
    expected_count: number;
    received_count: number;
    missing_count: number;
    validation_status: string;
  }> | null;
  error?: string;
}

export interface TranslatedUnit {
  text_id: string;
  source_text: string;
  translated_text: string;
  confidence: number;
}

export interface BatchResultsPayload {
  translations: TranslatedUnit[];
}

/** Locally persisted batch job entry (localStorage). */
export interface BatchJobEntry {
  jobId: string;
  fileName: string;
  sourceLang: string;
  targetLang: string;
  model: string;
  totalUnits: number;
  status: BatchJobStatus;
  completedUnits: number;
  createdAt: string;
  updatedAt: string;
  resultsUrl?: string;
  error?: string;
  /** Server-side history sort key (sk), used for deletion. */
  contentHash?: string;
}

export const isBatchTerminal = (status: string): boolean =>
  status === "completed" || status === "failed";

export const MODEL_OPTIONS = [
  { value: "gpt-4.1", label: "GPT-4.1" },
  { value: "gpt-5.4", label: "GPT-5.4" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
] as const;
